import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import moment from "moment";
import api from "../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./attendanceCalendar.css";

export default function StudentAttendanceCalendar() {
  const [students, setStudents] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [attendance, setAttendance] = useState([]);
  const [summary, setSummary] = useState({ present: 0, absent: 0 });

  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState("2025");

  // Fetch approved students
  useEffect(() => {
    api.get(`${process.env.REACT_APP_API_BASE_URL}students/approved`)
      .then((res) => setStudents(res.data))
      .catch((err) => console.error("Error fetching students", err));
  }, []);

  // Fetch attendance for selected student
  useEffect(() => {
    if (!selectedEmail) return;

    api.get(`${process.env.REACT_APP_API_BASE_URL}attendance/student/${selectedEmail}`)
      .then((res) => setAttendance(res.data || []))
      .catch((err) => console.error("Error fetching attendance", err));
  }, [selectedEmail]);

  // Update summary counts
  useEffect(() => {
    const filtered = attendance.filter((a) => {
      const date = moment(a.date);
      return (
        (selectedMonth === "All" || date.format("MM") === selectedMonth) &&
        date.format("YYYY") === selectedYear
      );
    });

    setSummary({
      present: filtered.filter((a) => a.status === "Present").length,
      absent: filtered.filter((a) => a.status === "Absent").length,
    });
  }, [attendance, selectedMonth, selectedYear]);

  const exportToExcel = () => {
    const rows = attendance.map((a) => ({
      Date: a.date,
      Status: a.status,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, `attendance_${selectedEmail}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Attendance Report", 14, 10);
    const rows = attendance.map((a) => [a.date, a.status]);
    doc.autoTable({
      head: [["Date", "Status"]],
      body: rows,
    });
    doc.save(`attendance_${selectedEmail}.pdf`);
  };

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const day = moment(date).format("YYYY-MM-DD");
    const record = attendance.find((a) => a.date === day);

    const isWeekend = [0, 6].includes(date.getDay());
    const classes = isWeekend ? "weekend-tile" : "";

    if (record?.status === "Present") {
      return <div className={`${classes} present-day`}>✅</div>;
    }
    if (record?.status === "Absent") {
      return <div className={`${classes} absent-day`}>❌</div>;
    }

    return <div className={`${classes} no-record`}>—</div>;
  };

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-xl font-bold">Student Attendance Calendar</h2>

      {/* Student Selector */}
      <div>
        <label className="block mb-1 font-medium">Select Student</label>
        <select
          className="border px-3 py-2 rounded w-full max-w-md"
          value={selectedEmail}
          onChange={(e) => setSelectedEmail(e.target.value)}
        >
          <option value="">-- Select Student --</option>
          {students.map((s) => (
            <option key={s._id} value={s.email}>
              {s.name} ({s.email})
            </option>
          ))}
        </select>
      </div>

      {selectedEmail && (
        <>
          {/* Filter & Export */}
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border rounded px-3 py-1"
            >
              <option value="All">All Months</option>
              {["05", "06", "07", "08", "09"].map((m) => (
                <option key={m} value={m}>{moment(m, "MM").format("MMMM")}</option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="border rounded px-3 py-1"
            >
              <option value="2025">2025</option>
            </select>

            <button onClick={exportToExcel} className="bg-green-600 text-white px-3 py-1 rounded">
              Export Excel
            </button>
            <button onClick={exportToPDF} className="bg-red-600 text-white px-3 py-1 rounded">
              Export PDF
            </button>
          </div>

          {/* Summary */}
          <div className="flex gap-6 mt-2">
            <div className="text-green-600 font-medium">✅ Present: {summary.present}</div>
            <div className="text-red-600 font-medium">❌ Absent: {summary.absent}</div>
          </div>

          {/* Calendar */}
          <Calendar
            tileContent={tileContent}
            minDate={new Date("2025-05-15")}
            maxDate={new Date("2025-09-15")}
          />
        </>
      )}
    </div>
  );
}